import { useLocation, useNavigate } from '@tanstack/react-router';
import { apiFetch, ApiError } from '@/lib/api';
import { scanDeuten } from '@/lib/scan';
import { useScanHosts } from '@/hooks/useScanHosts';
import { useToast } from '@/lib/toast';
import type { BestellungenResponse } from '@/types/dashboard';

// Was eine Eingabe in DAS Suchfeld bedeutet — Nummer, Zettel-Adresse,
// ISBN, Text — und wohin sie führt.
//
// Stand bis zur oberen Leiste (03.11.2026) in routes/index.tsx. Seitdem
// gibt es das Suchfeld an zwei Orten: am PC oben in der Leiste auf jeder
// Seite, am Telefon weiter über der Dashboard-Liste. Beide fragen hier,
// damit eine Nummer an beiden Orten dasselbe tut (Fallstrick 15 im
// Kleinen: zwei Kopien derselben Regel laufen auseinander).

// Ü wie in der Übungsbestellung. Die Nummer steht genauso auf dem Zettel
// und im QR-Code — bis hierher kannte die Auflösung nur das B.
const BESTELLNR_REGEX = /^[BÜ]-\d{4}-\d{4}$/u;
const SUFFIX_REGEX = /^\d{4}$/;
const ZIFFERN_KURZ_REGEX = /^\d{1,6}$/;

/**
 * Aus einer Eingabe die passenden Nummern-Kandidaten machen.
 *
 * Dieselbe Toleranz, die auch der Kurzlink /s/{nr} serverseitig kennt,
 * nur clientseitig: die ganze Nummer, das Kürzel ohne Jahr, und die
 * Ziffernfolge als Shop-Bestellnummer. Bewusst eine Liste in fester
 * Reihenfolge — die eigene Nummer schlägt die fremde.
 */
export function nummernKandidaten(eingabe: string): string[] {
  if (BESTELLNR_REGEX.test(eingabe)) return [eingabe];
  const kandidaten: string[] = [];
  if (SUFFIX_REGEX.test(eingabe)) {
    kandidaten.push(`B-${new Date().getUTCFullYear()}-${eingabe}`);
  }
  // Reine Ziffern sucht das Backend zusätzlich als Shopify-Bestellnummer.
  if (ZIFFERN_KURZ_REGEX.test(eingabe)) kandidaten.push(eingabe);
  return kandidaten;
}

export function useSuchsprung() {
  const navigate = useNavigate();
  const pfad = useLocation({ select: (l) => l.pathname });
  const toast = useToast();
  // Wessen QR-Codes als unsere gelten (A28) — der eigene Host immer,
  // Zusatz-Hosts aus den Einstellungen für die Zeit nach einem Umzug.
  const scanHosts = useScanHosts();

  /**
   * Tolerante Nummern-Auflösung: liefert die Bestellnummer oder null.
   *
   * Gesprungen wird nur bei GENAU einem Treffer. Bei mehreren wäre die
   * Wahl geraten, und wer den Zettel in der Hand hält, soll die Liste
   * sehen statt in einer fremden Bestellung zu landen.
   */
  async function nummerAufloesen(eingabe: string): Promise<string | null> {
    for (const kandidat of nummernKandidaten(eingabe)) {
      try {
        const res = await apiFetch<BestellungenResponse>(
          `/bestellungen.php?q=${encodeURIComponent(kandidat)}&include_archiviert=1`
        );
        if (res.total === 1) return res.bestellungen[0].bestellnr;
      } catch (e) {
        if (!(e instanceof ApiError)) throw e;
      }
    }
    return null;
  }

  function zurBestellung(bestellnr: string): void {
    void navigate({ to: '/bestellungen/$bestellnr', params: { bestellnr } });
  }

  /**
   * Den Begriff auf dem Dashboard suchen. Von dort aus bleibt die übrige
   * Filterung stehen; von einer anderen Seite aus beginnt die Liste mit
   * genau diesem Begriff.
   */
  function aufDemDashboard(wert: string): void {
    void navigate({
      to: '/',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      search: ((prev: any) => (pfad === '/' ? { ...prev, q: wert } : { q: wert })) as never
    });
  }

  /**
   * Was nach einem erfolgreichen Scan geschieht.
   *
   * Der gescannte Text wird gedeutet (lib/scan.ts), nicht ausgeführt. Eine
   * fremde Adresse wird gemeldet und NIE geöffnet: ein QR-Code ist ein
   * Aufkleber, den jeder anbringen kann.
   *
   * Auch der eigene Zettel-QR führt nicht über /s/{nr} zurück zum Server.
   * Das wäre ein harter Seitenwechsel mitten in der laufenden Anwendung —
   * gesprungen wird clientseitig, mit derselben Auflösung wie beim Tippen.
   */
  async function scanVerarbeiten(roh: string): Promise<void> {
    const deutung = scanDeuten(roh, scanHosts);
    if (deutung.art === 'fremd') {
      toast('error', 'Kein Tracking-Code.');
      return;
    }
    if (deutung.art === 'nummer') {
      const nr = await nummerAufloesen(deutung.wert);
      if (nr !== null) {
        toast('success', `Bestellung ${nr} geöffnet.`);
        zurBestellung(nr);
        return;
      }
    }
    // Zwei Wege enden hier: die ISBN vom Buchrücken — der
    // Auspacktisch-Fall, welche offenen Bestellungen wollen dieses Buch —
    // und die Nummer, zu der es keinen eindeutigen Treffer gab. Beide
    // Male ist die Suche die ehrliche Antwort; ein Scan soll nicht ins
    // Leere laufen.
    toast('success', `Gescannt: ${deutung.wert}`);
    aufDemDashboard(deutung.wert);
  }

  /** Enter im Suchfeld: Bestellnummer-Direktsprung, sonst Liste. */
  async function absenden(eingabe: string): Promise<void> {
    const trimmed = eingabe.trim();
    if (trimmed === '') return;

    /*
     * Ein Handscanner tippt in das Feld, das gerade den Fokus hat (A35,
     * #97) — und was er hineintippt, ist die ADRESSE vom Zettel, nicht die
     * Bestellnummer: https://tracking.buch-engel.com/s/B-2026-0001
     *
     * Umgeleitet wird nur, wenn scanDeuten() etwas ANDERES herausliest
     * als das, was dasteht. Bei „B-2026-0001", „1287" oder „Zauberberg"
     * ist der gedeutete Wert der eingetippte; sie laufen unverändert
     * weiter unten durch nummerAufloesen(). Nur unsere URL trägt eine
     * Nummer IN sich, und nur sie geht den Scan-Weg.
     */
    const deutung = scanDeuten(trimmed, scanHosts);
    if (deutung.art !== 'fremd' && deutung.wert !== trimmed) {
      await scanVerarbeiten(trimmed);
      return;
    }

    const nr = await nummerAufloesen(trimmed);
    if (nr !== null) {
      zurBestellung(nr);
      return;
    }
    aufDemDashboard(trimmed);
  }

  return { absenden, scanVerarbeiten };
}
